import React, { useRef, useState } from 'react';
import { authHeaders } from '../../services/auth';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Upload, PlayCircle, CheckCircle2, AlertCircle, Loader2, RefreshCw, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const Row = ({ label, value, tone = 'slate' }) => {
  const colors = {
    slate: 'text-slate-900',
    green: 'text-emerald-700',
    amber: 'text-amber-700',
    red: 'text-red-700',
    blue: 'text-blue-700',
  };
  return (
    <div className="flex justify-between items-baseline py-1 border-b border-slate-100 last:border-0">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`text-sm font-semibold ${colors[tone]}`}>{value}</span>
    </div>
  );
};

const send = async (file, dryRun) => {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${API}/api/super-admin/vehicles/bulk-epc-upload?dry_run=${dryRun}`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(),
    body: fd,
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.detail || 'Upload failed');
  return j;
};

export const SAEpcBulkUploadPanel = () => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dryResult, setDryResult] = useState(null);
  const [liveResult, setLiveResult] = useState(null);
  const [dryRunning, setDryRunning] = useState(false);
  const [liveRunning, setLiveRunning] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setDryResult(null);
    setLiveResult(null);
  };

  const reset = () => {
    setFile(null);
    setDryResult(null);
    setLiveResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const doDryRun = async () => {
    if (!file) { toast.error('Choose a CSV first'); return; }
    setDryRunning(true);
    setLiveResult(null);
    setDryResult(null);
    try {
      const j = await send(file, true);
      setDryResult(j);
      toast.success(`Dry run complete — ${j.counts.would_update} vehicle(s) would be updated`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDryRunning(false);
    }
  };

  const doApply = async () => {
    if (!dryResult) return;
    if (!window.confirm(
      `Apply EPC backfill to ${dryResult.counts.would_update} vehicle(s)? Existing EPC values will NOT be overwritten.`
    )) return;
    setLiveRunning(true);
    try {
      const j = await send(file, false);
      setLiveResult(j);
      toast.success(`Applied — ${j.counts.updated} vehicle(s) updated`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLiveRunning(false);
    }
  };

  const shown = liveResult || dryResult;
  const isDry = shown && !liveResult;
  const c = shown?.counts || {};

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-6 mt-6"
      data-testid="sa-epc-bulk-upload-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Upload className="w-5 h-5 text-indigo-700" />
          <h3 className="text-lg font-semibold text-slate-900">Bulk EPC / RFID Upload</h3>
        </div>
        {(file || shown) && (
          <button
            onClick={reset}
            className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1"
            data-testid="sa-epc-bulk-reset"
          >
            <RefreshCw className="w-3 h-3" /> Reset
          </button>
        )}
      </div>

      <p className="text-xs text-slate-500 mb-4">
        Upload a CSV with columns <code className="bg-slate-100 px-1 rounded">Plate Number, Villa, Type, EPC</code>
        {' '}(headers are case-insensitive). Matching is by plate number. Only <strong>blank</strong> EPC fields are filled — existing values are <strong>never overwritten</strong>.
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <label
          htmlFor="sa-epc-bulk-file"
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm cursor-pointer ${file ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'}`}
          data-testid="sa-epc-bulk-file-label"
        >
          <FileText className="w-4 h-4" />
          {file ? file.name : 'Choose CSV file'}
        </label>
        <input
          ref={fileRef}
          id="sa-epc-bulk-file"
          type="file"
          accept=".csv,text/csv"
          onChange={onFile}
          className="hidden"
          data-testid="sa-epc-bulk-file"
        />
        <Button
          onClick={doDryRun}
          disabled={!file || dryRunning || liveRunning}
          data-testid="sa-epc-bulk-dryrun"
          className="bg-slate-700 hover:bg-slate-800 text-white disabled:opacity-50"
        >
          {dryRunning ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-1.5" />}
          Run dry preview
        </Button>
        <Button
          onClick={doApply}
          disabled={!dryResult || dryRunning || liveRunning || (dryResult.counts.would_update === 0)}
          data-testid="sa-epc-bulk-apply"
          className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
        >
          {liveRunning ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
          Apply {dryResult?.counts?.would_update ? `(${dryResult.counts.would_update})` : ''}
        </Button>
      </div>

      {shown && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="sa-epc-bulk-results">
          <div className="flex items-center gap-2 mb-2">
            <Badge className={`${isDry ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'} border-0 text-[10px]`}>
              {isDry ? 'DRY PREVIEW' : 'APPLIED'}
            </Badge>
          </div>
          <Row label="Parsed CSV rows"                value={c.parsed ?? 0}                  tone="slate" />
          <Row label={isDry ? 'Would update' : 'Updated'} value={(isDry ? c.would_update : c.updated) ?? 0} tone="green" />
          <Row label="Skipped (already set)"          value={c.skipped_already_set ?? 0}     tone="blue" />
          <Row label="Skipped (plate not in DB)"      value={c.skipped_plate_not_found ?? 0} tone="amber" />
          <Row label="Skipped (duplicate plate)"      value={c.skipped_duplicate_plate ?? 0} tone="amber" />
          <Row label="Errors"                         value={c.errors ?? 0}                  tone={c.errors ? 'red' : 'slate'} />

          {(shown.samples_update?.length > 0) && (
            <div className="mt-3">
              <div className="text-xs font-semibold text-slate-600 mb-1.5">
                {isDry ? 'Sample of updates (first 10):' : 'Updated samples:'}
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto text-[11px] font-mono bg-white rounded border border-slate-200 p-2">
                {shown.samples_update.map((s, i) => (
                  <div key={i} className="text-slate-700 truncate">
                    <span className="text-slate-900 font-semibold">{s.plate}</span>
                    {' · villa '}<span className="text-blue-700">{s.villa || '—'}</span>
                    {' → '}<span className="text-emerald-700">{s.new_epc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(shown.samples_plate_not_found?.length > 0) && (
            <div className="mt-3">
              <div className="text-xs font-semibold text-amber-700 mb-1.5">
                Plates not found in DB (first 10):
              </div>
              <div className="space-y-1 max-h-32 overflow-y-auto text-[11px] font-mono bg-white rounded border border-amber-200 p-2">
                {shown.samples_plate_not_found.map((s, i) => (
                  <div key={i} className="text-slate-700 truncate">
                    <span className="text-slate-900 font-semibold">{s.plate}</span>
                    {s.villa && <span className="text-slate-500"> · villa {s.villa}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(shown.samples_skip_already_set?.length > 0) && (
            <div className="mt-3">
              <div className="text-xs font-semibold text-blue-700 mb-1.5">
                Plates with existing EPC (preserved, first 5):
              </div>
              <div className="space-y-1 max-h-28 overflow-y-auto text-[11px] font-mono bg-white rounded border border-blue-200 p-2">
                {shown.samples_skip_already_set.map((s, i) => (
                  <div key={i} className="text-slate-700 truncate">
                    <span className="text-slate-900 font-semibold">{s.plate}</span>
                    {' keep '}<span className="text-emerald-700">{s.existing}</span>
                    {' (csv had '}<span className="text-slate-400">{s.csv}</span>{')'}
                  </div>
                ))}
              </div>
            </div>
          )}

          {isDry && c.would_update > 0 && (
            <div className="mt-3 flex items-start gap-2 p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              Dry run complete. Click <strong>Apply</strong> above to write to MongoDB &amp; sync to Airtable.
            </div>
          )}
          {isDry && c.would_update === 0 && (
            <div className="mt-3 flex items-start gap-2 p-2 rounded bg-slate-100 border border-slate-200 text-xs text-slate-700">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              Nothing to update. Either all rows are already set or no plates matched.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SAEpcBulkUploadPanel;
